import { EventApi } from '@fullcalendar/core';

/**
 * Minutes of grid shown above the target event so the title isn't flush with the
 * scrollport top (2h of context above the nearest event).
 */
const EVENT_SCROLL_LEAD_MINUTES = 120;

export interface TimedEventStart {
  start: Date | null;
  end: Date | null;
  allDay: boolean;
}

/**
 * Picks the timed event whose start is nearest to `now`. An in-progress event
 * (start ≤ now < end) wins over any other distance.
 */
export function findNearestEventStart(
  events: readonly TimedEventStart[],
  now: Date = new Date(),
): Date | null {
  let best: Date | null = null;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const event of events) {
    if (event.allDay || !event.start) continue;

    const start = event.start;
    const end = event.end;
    const inProgress = !!end && start.getTime() <= now.getTime() && now.getTime() < end.getTime();
    const dist = inProgress ? 0 : Math.abs(start.getTime() - now.getTime());

    if (dist < bestDist || (dist === bestDist && best !== null && start.getTime() < best.getTime())) {
      bestDist = dist;
      best = start;
    }
  }

  return best;
}

/**
 * Formats a local wall-clock time as FullCalendar `scrollToTime` duration input
 * (`HH:mm:ss`), shifted back by {@link EVENT_SCROLL_LEAD_MINUTES} (clamped at 00:00:00).
 */
export function scrollTimeForEventStart(start: Date): string {
  const leadMs = EVENT_SCROLL_LEAD_MINUTES * 60 * 1000;
  const targetMs = Math.max(
    start.getTime() - leadMs,
    new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime(),
  );
  const target = new Date(targetMs);
  const hours = `${target.getHours()}`.padStart(2, '0');
  const minutes = `${target.getMinutes()}`.padStart(2, '0');
  const seconds = `${target.getSeconds()}`.padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

/** Convenience for FullCalendar's {@link EventApi} list. */
export function scrollTimeForNearestEvent(
  events: readonly EventApi[],
  now: Date = new Date(),
): string | null {
  const nearest = findNearestEventStart(events, now);
  return nearest ? scrollTimeForEventStart(nearest) : null;
}
